import { useSyncExternalStore } from 'react';

/**
 * Where a notification actually wants to take you.
 *
 * "Open" used to mean "land on the right SECTION and start looking": a
 * feedback alert dropped a super admin on the Administration page, three
 * clicks from the feedback it was about, and a documents alert threw you out
 * of the portal into Parallel. A notification should finish the journey it
 * started — the exact panel, or the exact module of the exact case.
 *
 * Two tiny stores, both read with useSyncExternalStore so any view can react
 * without prop-drilling through the shell.
 */

/* ── Administration: which tab, and which Portal-management page ── */
export interface AdminFocus {
  /** Administration's own tab id (see AdminTab in AdminView). */
  tab: string;
  /** Portal management sub-page, when the destination lives inside it. */
  page?: string;
  /** Bumped on every request so the same destination twice still fires. */
  n: number;
}

const adminListeners = new Set<() => void>();
let adminFocus: AdminFocus | null = null;

export function setAdminFocus(tab: string, page?: string, item?: string | null) {
  adminFocus = { tab, page, n: (adminFocus?.n || 0) + 1 };
  adminItem = item || null;
  adminListeners.forEach((l) => l());
}
/* ── THE EXACT ENTRY, not only its page (24 Sept) ──
   A feedback notification carries the feedback's id; the panel reads it once
   its list has arrived, opens the right tab and scrolls to that row. */
let adminItem: string | null = null;
export function takeAdminItem(): string | null {
  const v = adminItem;
  adminItem = null;
  return v;
}
export function peekAdminItem(): string | null { return adminItem; }
/** Read it once and clear it — a destination is consumed on arrival. */
export function takeAdminFocus(): AdminFocus | null {
  const f = adminFocus;
  adminFocus = null;
  return f;
}
export function useAdminFocus(): AdminFocus | null {
  return useSyncExternalStore(
    (cb) => { adminListeners.add(cb); return () => adminListeners.delete(cb); },
    () => adminFocus,
    () => adminFocus,
  );
}

/* ── A case page: which module to open on ── */
const secListeners = new Set<() => void>();
let sectionFocus: { id: string; n: number } | null = null;

/** Ask the next case page to scroll to — and flash — one of its modules. */
export function setTicketSection(id: string) {
  sectionFocus = { id, n: (sectionFocus?.n || 0) + 1 };
  secListeners.forEach((l) => l());
}
export function useTicketSection(): { id: string; n: number } | null {
  return useSyncExternalStore(
    (cb) => { secListeners.add(cb); return () => secListeners.delete(cb); },
    () => sectionFocus,
    () => sectionFocus,
  );
}
export function clearTicketSection() {
  sectionFocus = null;
  secListeners.forEach((l) => l());
}
